<script setup lang="ts">
import { getAd, getSponsorLink } from '@/api/ads'
import { type PromptAd, asPromptAd } from '~~/src/types/ad'

defineOptions({
  name: 'AppPubCard',
})

const followedTopicsLoaded = useFollowedTopicsLoaded()
const adsListing = useAdsListing()

const target = ref<HTMLElement>()
const isVisible = ref(false)
const banner = ref<PromptAd | null>()
const tryingToLoad = ref(true)
const hasError = ref(false)

const checkElementVisibility = () => {
  if (!target.value)
    return false

  if (banner.value)
    return true

  const rect = target.value.getBoundingClientRect()
  return (
    rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= ((window.innerHeight || document.documentElement.clientHeight) + 32)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

const startInterval = () => {
  const interval = setInterval(() => {
    const isTargetVisible = checkElementVisibility()
    if (isTargetVisible) {
      isVisible.value = checkElementVisibility()
      clearInterval(interval)
    }
  }, 50)
}

onMounted(async () => {
  setTimeout(() => {
    // if it didn't start in 3000ms, force start (user is not logged in)
    if (!followedTopicsLoaded.value)
      followedTopicsLoaded.value = true
  }, 3000)

  if (followedTopicsLoaded.value)
    startInterval()
})

whenever(followedTopicsLoaded, () => {
  setTimeout(startInterval, 100)
})

const loadAd = async () => {
  hasError.value = false
  try {
    banner.value = asPromptAd(await getAd({ type: 'in-content', not_id: undefined }))
  }
  catch (_) {
    hasError.value = true
    banner.value = null
  }

  if (banner.value) {
    adsListing.value.push(banner.value?.id)
  }
  else {
    if (adsListing.value && adsListing.value.length) {
      adsListing.value = []
      loadAd()
    }
    else {
      tryingToLoad.value = false
    }
  }
}

whenever(isVisible, loadAd)

const coverImage = computed(() => {
  if (!banner.value)
    return
  if (banner.value?.banner) {
    return optimizeImage(banner.value.banner, {
      width: 450,
    })
  }

  if (banner.value?.user?.avatar) {
    return optimizeImage(banner.value?.user?.avatar, {
      width: 450,
    })
  }

  const imageStyle = 'initials'
  const fontSize = 28
  const backgroundColors = ['F499D5', 'D496F1', 'BABAFF', '858AFE', 'D8F0BA', 'D7F7D0', 'D3F2FF']

  return `https://api.dicebear.com/9.x/${imageStyle}/svg?seed=${banner.value.user?.name || banner.value.title}&fontSize=${fontSize}&backgroundColor=${backgroundColors.join(',')}&backgroundType=solid&shape1=d,e,g,i,n,r,t,a`
})
</script>

<template>
  <div ref="target">
    <template v-if="!hasError">
      <div
        v-if="banner "
      >
        <NuxtLink
          :to="getSponsorLink(banner.id)" target="_blank"
          class="flex flex-col gap-1 md:gap-2 group transition-all"
          :title="banner.title"
        >
          <div class="relative border border-soft-gray rounded-lg overflow-hidden">
            <img :src="coverImage" :alt="banner.title" class="w-full min-w-[166px] min-h-[110px] object-cover object-center aspect-3/2 rounded-2 bg-grayLighter">

            <div class="absolute left-2 bottom-2 py-[2px] pl-[4px] pr-[6px] h-[20px] bg-white/80 rounded-1 backdrop-blur-[8px] drop-shadow-[0_0_4px_rgba(0,0,0,0.05)]">
              <div flex gap-1 items-center>
                <TextV2 class="text-[10px] font-bold leading-none font-bold color-dark-gray">
                  Ad
                </TextV2>
                <UserChip v-if="banner.user" :show-borders="true" :user="banner.user" :should-redirect="false" variant="text" :avatar-size="14" />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-0.5">
            <TextV2 class="text-[14px] <md:text-[12px] font-bold truncate" :title="banner.title">
              {{ banner.title }}
            </TextV2>

            <div class="flex items-center gap-3 sm:gap-2.5 min-h-5.5">
              <TextV2 class="body-12 font-bold color-dark-gray">
                <div>
                  {{ banner.cta || 'Open' }}
                  <span i-ph-arrow-up-right-bold class="relative inline-block text-[15px] mt-[3px] ml-[2px] align-top" />
                </div>
              </TextV2>
            </div>
          </div>
        </NuxtLink>
      </div>
      <div v-else-if="tryingToLoad" class="flex flex-col gap-1 md:gap-2 group transition-all">
        <div
          class="w-full min-w-[166px] min-h-[110px] aspect-3/2 rounded-2 bg-grayLighter animate-pulse"
        />

        <div class="flex flex-col gap-0.5">
          <div class="w-full h-4 bg-grayLighter rounded animate-pulse" />
          <div class="w-full h-4 bg-grayLighter rounded animate-pulse" />
        </div>
      </div>
    </template>
    <slot v-else name="fallback" />
  </div>
</template>
